/**
 * HwCounterPicker — the Handwritten Ctr picker at step 3 of the generate wizard.
 *
 * A native <select> cannot colour its options on phones (Android draws its own list), so
 * this opens a sheet of its own: each counter with whether it is free at shift start and
 * until when (green), or who holds it and until when (red).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NUM_SLOTS } from "@/config/modeConfig";
import { UI_SCALE, popupMaxH } from "@/utils/uiScale";
import { vacancyText, type CounterVacancy } from "@/utils/hwCounterVacancy";

export interface HwCounterOption {
  /** What the officer row stores — the counter's label. */
  value: string;
  /** What to show for it when that is not the stored value ("AC35" for "35", "Auto" for ""). */
  label?: string;
  vacancy?: CounterVacancy;
}

interface HwCounterPickerProps {
  value: string;
  options: HwCounterOption[];
  onChange: (value: string) => void;
  /** HHMM of an exclusive end slot. */
  slotTime: (slot: number) => string;
  /** End slot of the handwritten counter this officer would get — a free run shorter than it is flagged. */
  neededSlots: number;
  /** Whether the printed roster is known; without it only other handwritten counters are. */
  hasRoster: boolean;
  title: string;
  testId?: string;
  /** A heavier border and a chevron, so it reads as a dropdown you can tap (the SOS table's counters). */
  emphasis?: boolean;
}

const GREEN = "#15803d";
const RED = "#b91c1c";
const AMBER = "#b45309";

const colourOf = (v: CounterVacancy | undefined) => (!v ? "#6b7280" : v.state === "free" ? GREEN : RED);

export function HwCounterPicker({
  value, options, onChange, slotTime, neededSlots, hasRoster, title, testId, emphasis,
}: HwCounterPickerProps) {
  const [open, setOpen] = useState(false);
  const selectedRef = useRef<HTMLButtonElement | null>(null);
  const totalSlots = NUM_SLOTS;
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    selectedRef.current?.scrollIntoView({ block: "center" });
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const pick = (v: string) => { onChange(v); setOpen(false); };
  const narrow = typeof window !== "undefined" && window.innerWidth < 640;

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        aria-haspopup="dialog"
        aria-label={title}
        title={current?.vacancy ? vacancyText(current.vacancy, slotTime, totalSlots) : undefined}
        onClick={() => setOpen(true)}
        style={{
          width: "100%", padding: emphasis ? "3px 18px 3px 4px" : "3px", borderRadius: "4px", fontSize: "13px", textAlign: "center",
          minHeight: 24, cursor: "pointer", background: "white", position: "relative",
          border: `${emphasis ? 2 : 1}px solid ${current?.vacancy && value ? colourOf(current.vacancy) : emphasis ? "#334155" : "#ddd"}`,
          color: current?.vacancy && value ? colourOf(current.vacancy) : "#333",
          fontWeight: value ? 600 : 400,
        }}
      >
        {current?.label ?? (value || " ")}
        {emphasis && (
          <span aria-hidden style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: "#334155" }}>▼</span>
        )}
      </button>

      {open && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 4600,
            display: "flex", justifyContent: "center", alignItems: narrow ? "flex-end" : "center",
            padding: narrow ? 0 : 16, overscrollBehavior: "contain",
          }}
        >
          <div
            style={{
              background: "white", width: "100%", maxWidth: 440, zoom: UI_SCALE, maxHeight: popupMaxH(narrow ? 70 : 80, "dvh"),
              display: "flex", flexDirection: "column", overflow: "hidden",
              borderRadius: narrow ? "16px 16px 0 0" : 12, boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            }}
          >
            <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid #e5e7eb", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: "#1a1a2e" }}>{title}</div>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                  style={{ border: "none", background: "#f1f5f9", borderRadius: 8, width: 36, height: 36, fontSize: 16, cursor: "pointer", color: "#475569" }}
                >
                  ✕
                </button>
              </div>
              {!hasRoster && (
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  No printed roster loaded, so only other handwritten counters are shown.
                </div>
              )}
            </div>
            <div style={{ overflowY: "auto", minHeight: 0, paddingBottom: "env(safe-area-inset-bottom)" }}>
              {options.map((o) => {
                const selected = o.value === value;
                const v = o.vacancy;
                const short = v?.state === "free" && v.untilSlot < neededSlots;
                return (
                  <button
                    key={o.value || "none"}
                    type="button"
                    ref={selected ? selectedRef : undefined}
                    data-testid={testId ? `${testId}-opt-${o.value || "none"}` : undefined}
                    onClick={() => pick(o.value)}
                    style={{
                      display: "flex", alignItems: "center", width: "100%", textAlign: "left",
                      padding: "12px 16px", border: "none", borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                      background: selected ? "#dbeafe" : "white", minHeight: 52,
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 15, fontWeight: selected ? 700 : 600, color: "#111827" }}>
                        {o.label ?? (o.value || "None")}
                      </span>
                      {o.value && v && (
                        <span style={{ display: "block", fontSize: 12, color: colourOf(v), marginTop: 1 }}>
                          {v.state === "free" ? "✓ " : "✕ "}{vacancyText(v, slotTime, totalSlots)}
                        </span>
                      )}
                      {short && (
                        <span style={{ display: "block", fontSize: 11, color: AMBER, marginTop: 1 }}>
                          Taken before this officer's handwritten counter ends ({slotTime(neededSlots)})
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
